<template>
<div class="container-fluid p-0">
    <div class="row">
        <CCol sm="12">
            <CCard>
                <CCardBody>
                    <div class="d-flex mb-4 justify-content-between align-items-center">
                        <!-- Title -->
                        <span v-if="name"><b>{{ $t('guides.guideIntro') }}</b> {{name}}</span>
                    </div>
                    
                    <div class="guide-form-container">
                        <div class="row">
                            <!-- Name -->
                            <div class="col-12 col-lg-6 mb-4">
                                <div role="group" class="form-group">
                                    <label>{{ $t('nameMandatory') }}</label>
                                    <CInput 
                                        type="text" 
                                        v-model="guide.name" 
                                        :invalid-feedback="errorOutput['Name']"
                                        :is-valid="errorValidator('Name')"
                                        @blur="validateField('Name')">
                                    </CInput>
                                </div>
                            </div>

                            <!-- Email -->
                            <div class="col-12 col-lg-6 mb-4">
                                <div role="group" class="form-group">
                                    <label>{{ $t('emailMandatory') }}</label>
                                    <CInput 
                                        type="email" 
                                        v-model="guide.email" 
                                        :invalid-feedback="errorOutput['Email']"
                                        :is-valid="errorValidator('Email')"
                                        @blur="validateField('Email')">
                                    </CInput>
                                </div>
                            </div>

                            <!-- Phone -->
                            <div class="col-12 col-lg-6 mb-4">
                                <div role="group" class="form-group">
                                    <label>{{ $t('phoneMandatory') }}</label>
                                    <CInput 
                                        type="tel" 
                                        v-model="guide.phone" 
                                        :invalid-feedback="errorOutput['Phone']"
                                        :is-valid="errorValidator('Phone')"
                                        @blur="validateField('Phone')">
                                    </CInput>
                                </div>
                            </div>

                            <!-- Active Status -->
                            <div class="col-12 col-lg-6 mb-4">
                                <div role="group" class="form-group">
                                    <div class="form-check">
                                        <input 
                                            type="checkbox" 
                                            id="isActive" 
                                            v-model="guide.isActive"
                                            class="form-check-input">
                                        <label class="form-check-label" for="isActive">
                                            {{ $t('guides.active') }}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Form Actions -->
                    <div class="form-actions mt-4">
                        <CButton 
                            color="primary" 
                            @click="submitForm" 
                            :disabled="loading"
                        >
                            <span v-if="loading" class="spinner-border spinner-border-sm mr-2"></span>
                            {{ $t('save') }}
                        </CButton>
                        <CButton 
                            color="secondary" 
                            @click="cancelForm" 
                            class="ml-2"
                        >
                            {{ $t('cancel') }}
                        </CButton>
                    </div>
                </CCardBody>
            </CCard>
        </CCol>
    </div>
</div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { CInput, CButton, CCol, CCard, CCardBody } from '@coreui/vue';
import router from '@/router';

export default {
  name: 'GuidesForm',
  components: {
    CInput,
    CButton,
    CCol,
    CCard,
    CCardBody
  },
  props: {
    guideId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      guide: {
        name: '',
        email: '',
        phone: '',
        isActive: true
      },
      errorOutput: {},
      triedSubmit: false,
      touchedFields: {}
    }
  },
  computed: {
    ...mapState('guides', ['getGuideByIdOutput']),
    name() {
      return this.guide.name || '';
    }
  },
  async mounted() {
    if (this.guideId) {
      await this.loadGuide();
    }
  },
  methods: {
    ...mapActions('guides', ['getById', 'create', 'update']),
    
    async loadGuide() {
      try {
        this.loading = true;
        await this.getById(this.guideId);
        
        if (this.getGuideByIdOutput && this.getGuideByIdOutput.data) {
          const data = this.getGuideByIdOutput.data;
          this.guide = {
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            isActive: data.isActive !== undefined ? data.isActive : true
          };
        }
      } catch (error) {
        console.error('Error loading guide:', error);
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: this.$t('guides.loadError')
        });
      } finally {
        this.loading = false;
      }
    },
    
    async submitForm() {
      try {
        this.loading = true;
        this.triedSubmit = true;
        this.errorOutput = {};
        
        // Validate form
        if (!this.validateForm()) {
          this.loading = false;
          return;
        }
        
        const formData = { ...this.guide };
        
        if (this.guideId) {
          await this.update({ id: this.guideId, ...formData });
        } else {
          await this.create(formData);
        }
        
        this.$emit('form-submitted');
        this.$store.dispatch('alert/setAlert', {
          type: 'success',
          message: this.$t('guides.saveSuccess')
        });
        
      } catch (error) {
        console.error('Error saving guide:', error);
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: this.$t('guides.saveError')
        });
      } finally {
        this.loading = false;
      }
    },
    
    validateForm() {
      let isValid = true;
      this.errorOutput = {};
      
      // Validate name
      if (!this.guide.name || this.guide.name.trim() === '') {
        this.errorOutput['Name'] = this.$t('validation.required');
        isValid = false;
      }
      
      // Validate email
      if (!this.guide.email || this.guide.email.trim() === '') {
        this.errorOutput['Email'] = this.$t('validation.required');
        isValid = false;
      } else if (!this.isValidEmail(this.guide.email)) {
        this.errorOutput['Email'] = this.$t('validation.invalidEmail');
        isValid = false;
      }
      
      // Validate phone
      if (!this.guide.phone || this.guide.phone.trim() === '') {
        this.errorOutput['Phone'] = this.$t('validation.required');
        isValid = false;
      }
      
      return isValid;
    },
    
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    
    errorValidator(field) {
      // Only show validation state if user tried to submit or field was touched
      if (!this.triedSubmit && !this.touchedFields[field]) {
        return null;
      }
      return !this.errorOutput[field];
    },
    
    validateField(field) {
      this.touchedFields[field] = true;
      
      if (field === 'Name') {
        if (!this.guide.name || this.guide.name.trim() === '') {
          this.errorOutput['Name'] = this.$t('validation.required');
        } else {
          delete this.errorOutput['Name'];
        }
      } else if (field === 'Email') {
        if (!this.guide.email || this.guide.email.trim() === '') {
          this.errorOutput['Email'] = this.$t('validation.required');
        } else if (!this.isValidEmail(this.guide.email)) {
          this.errorOutput['Email'] = this.$t('validation.invalidEmail');
        } else {
          delete this.errorOutput['Email'];
        }
      } else if (field === 'Phone') {
        if (!this.guide.phone || this.guide.phone.trim() === '') {
          this.errorOutput['Phone'] = this.$t('validation.required');
        } else {
          delete this.errorOutput['Phone'];
        }
      }
    },
    
    cancelForm() {
      router.push({ name: 'Guides' });
    }
  }
}
</script>

<style scoped>
.guide-form-container {
  padding: 1rem 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

@media (max-width: 768px) {
  .guide-form-container {
    padding: 0.5rem 0;
  }
}
</style>

